/**
 * ConfirmQueue: Kith's proposals, one decision each. Kith reads a posted
 * note and proposes structured changes (the medication family,
 * appointments, conditions, housing, risk, providers, contacts, hospital
 * events, directives, insurance, activities, allergies, and the SPICE
 * session types); the care manager confirms or dismisses each. Nothing
 * lands in the record without a person's yes.
 *
 * Card grammar: the care manager's own words as a quiet quotation (the
 * receipt that Kith read, not invented), then the facts it became on
 * labelled lines (a dl, one fact per line: a name as the record will
 * store it, a one-line value with its capital and its field's
 * abbreviations in their standard form, a note, an address or an email as
 * written, dates in prose), then one button per outcome, each naming what it does
 * ("Add medication", "Record ER visit"). A card with more than one way to
 * confirm (a choice card) shows every outcome as its own button in one
 * row: primary, then secondaries, then the ghost Dismiss. One click sends
 * one request. The typed-field editor rests behind "Adjust"; edits travel
 * on every confirming button whether or not it is open, and the labels
 * and facts read the edited values. The rules for buttons, facts and the
 * header live in @/lib/confirmQueueActions.
 *
 * The header's bulk action names its outcome and its count ("Add 3 to
 * the record", "Add all 4 to the record") and adds only high-confidence
 * cards with one outcome. Choice cards, schedule changes, cards waiting
 * for a date and low-confidence cards keep their own buttons; a quiet
 * line beside the button counts the high-confidence cards it leaves
 * ("1 needs your choice, 1 to confirm on its own"). Low-confidence cards
 * sit in their own section under the high ones.
 *
 * A schedule change (spice_status_change) pauses the client's visits,
 * which needs manage_client_identity. A viewer without it sees the card
 * stay open with "The office updates the schedule." and no buttons; the
 * header neither adds nor counts it for that viewer.
 *
 * A clinical proposal (any type but SPICE's) changes the client's record,
 * which only a seat that keeps it may do (canWriteClinicalRecord; the
 * server's keepsClinicalRecord, LP-48). A note writer who does not keep
 * the record (a SPICE-only therapist) sees the card stay open with "The
 * office adds this to the record." and no buttons, the same way, until
 * the note is voided or superseded, which dismisses its open proposals.
 *
 * A contact the note names as the billing contact confirms without that
 * flag for a viewer who cannot choose who receives the bill (no
 * set_bill_to); the card says the office sets it, and its Adjust shows
 * the record form's office line in place of the Billing Contact checkbox.
 *
 * The queue lists both open states: proposed rows and held rows (status
 * held_for_review, a record update Olive could not read cleanly). A held
 * row keeps only the fields its own editor shows, each with a valid
 * value, so what the card shows is what a confirm sends. Its card opens
 * with the editor open and reads one sentence ("Olive couldn't read this
 * update cleanly. Adjust it or dismiss it.") and a "Set aside:" line
 * naming anything it could not use; the stored reason never shows. Its
 * outcome buttons wait until every required field is filled, with a line
 * naming what is missing, and Dismiss never waits. A held row with no
 * editor (a SPICE or unknown type) offers Dismiss only. A confirm the
 * server still refuses with a 400 shows a line on the card naming the
 * field, never the banner. The header never adds a held row and counts
 * it as "Olive couldn't read".
 *
 * Resolve is optimistic: the card leaves the queue at once. A failed
 * resolve puts back only its own card, with an ErrorBanner (or, for a
 * refused held confirm, the card's own line). Once no resolve is in
 * flight, the list is read from the server again, so the cards and their
 * match data are the server's, and Your day's read is marked stale so
 * its count follows.
 *
 * Confirming settles the queue (the remaining cards close ranks with
 * animate-settle-up, inert under reduced motion) and announces the
 * landing (CONFIRM_LANDED_EVENT) so the destination record card, where
 * visible, pulses once.
 *
 * One vocabulary: the type labels, facts and typed fields read the
 * PROPOSAL_PAYLOAD_SPECS registry (mirrored in @/lib/noteVocabulary; the
 * browser cannot import api/_shared). The card never hand-lists a field
 * name, and an unknown type still renders its payload rather than failing.
 *
 * A typed card is its record form's card (recordFormWords.ts, through
 * recordProjectionForType): the form's field order and words, its value
 * words and select openings, the fields a value shows or requires (an
 * appointment's one date, with its status; a medication's Ended while
 * discontinued), the dates that are past by nature, a medication's When
 * as the form's ticks, and the form's care-team picker for a
 * medication's prescriber, an appointment's provider and a hospital
 * stay's facility. A card whose values require an empty date waits, as a
 * held card waits for its required fields.
 *
 * The provider_new card carries a confirm-time Directory link: "Link a
 * Directory profile" opens the house DirectorySearch; the pick travels in
 * the confirm payload as the directory arm (the provider writer validates
 * it). The pick fills no typed field: the card offers only what the
 * provider writer stores (name, role, office phone, office address), and
 * specialty and organization read from the linked Directory entry.
 */

import { housingConfirmLine, housingNewPlaceLine, housingWaitMessage, proposalHold } from '@/lib/proposalHolds';
import { errorCodeOf, serverSentence } from '@/lib/errorCopy';
import { AddressFields } from '@/components/clientRecord/AddressesCard';
import { Fragment, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canEditClientSchedule, canSetBillTo, canWriteClinicalNotes, canWriteClinicalRecord } from '@/lib/capabilities';
import {
  notesApi,
  type ProposalAction,
  type ProposalsResponse,
  type ProposalWire,
} from '@/lib/notes';
import {
  editableFields,
  fieldApplies,
  formFields,
  specForType,
  DEFAULT_RISK_CATEGORIES,
  type NoteProposalType,
  type ProposalPayloadSpec,
} from '@/lib/noteVocabulary';
import { humanizeToken } from '@/lib/fieldLabels';
import { isSpiceNoteProposalType, SPICE_TYPE_LABELS } from '@/lib/spiceNoteVocabulary';
import {
  SET_ASIDE_LABEL,
  appliesOnCard,
  billToLeftToOffice,
  bulkAction,
  cardActions,
  cardFacts,
  cardDefault,
  cardFieldLabel,
  cardValueLabel,
  cleanHeldProposal,
  effective,
  heldFailureLine,
  heldMissing,
  heldMissingLine,
  heldSentence,
  isHeldForReview,
  scalar,
  setAsideLine,
  setAsideOf,
  type CardAction,
} from '@/lib/confirmQueueActions';
import {
  CONFIRM_LANDED_EVENT,
  proposalDestinationTestId,
  type ConfirmLandedDetail,
} from '@/lib/gaspSummary';
import { ApiError } from '@/lib/api';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import { AppointmentWhere } from '@/components/clientRecord/AppointmentWhere';
import { entryAddress, holdsPlace, providerPlace, type ProviderPlace } from '@/components/clientRecord/appointmentPlace';
import { contactEmailProblem } from '@/lib/clientBillingContacts';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import { BillingHint, CheckboxRow } from '@/components/primitives/CheckboxRow';
import { formatPhoneDisplay, formatPhoneInput, normalizePhone } from '@/lib/phone';
import { textBoxProps } from '@/lib/textBoxProps';
import { ErrorBanner } from '@/components/ErrorBanner';
import { FLD, FLD_TEXTAREA_GROW } from '@/components/primitives/modalFormStyles';
import { nameCase as toNameCase } from '@/lib/valueCase';
import { DirectorySearch } from '@/components/clientRecord/AddProviderDialog';
import { SelectField } from '@/components/primitives/SelectField';
import { TickGroup } from '@/components/primitives/TickGroup';
import { CareTeamCombobox, type CareTeamPick } from '@/components/clientRecord/CareTeamCombobox';
import { cachedClientRecord } from '@/lib/clientRecordApi';
import { practiceTodayIso } from '@/lib/practiceDate';
import {
  RECORD_FIELD_ORDER,
  browserValueWhenHidden,
  careTeamFieldOf,
  isPastByNature,
  recordProjectionForType,
  requiredWhen,
  selectOpening,
} from '@/lib/recordFormWords';
import { DateInput } from '@/components/primitives/DateInput';

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.8;

// ---------------------------------------------------------------------
// Registry-driven presentation. Type labels, facts and typed fields all
// read PROPOSAL_PAYLOAD_SPECS (via @/lib/noteVocabulary); no second copy.
// ---------------------------------------------------------------------

/**
 * The proposal types whose card renders the typed-field editor under
 * Adjust (each field prefilled and editable, one control per registry
 * field by kind: string, date, enum, boolean, risk category). Directive's
 * direct-entry-only code_status_values set is left out by formFields:
 * proposals stay single-value and the writer merges them.
 */
const TYPED_TYPES: ReadonlySet<NoteProposalType> = new Set<NoteProposalType>([
  'medication_add',
  'medication_change',
  'medication_discontinue',
  'appointment_past',
  'appointment_upcoming',
  'condition_add',
  'condition_change',
  'hospital_event',
  'directive_change',
  'coverage_change',
  'provider_new',
  'contact_change',
  'housing_change',
  'risk_change',
  'functional_status_change',
  'allergy_change',
]);

/**
 * The chip accent per type: presentation only, not vocabulary. Keyed by
 * the registry type names; anything unlisted reads 'neutral'. Allergies
 * are patient-safety information and carry the caution accent.
 */
const TYPE_CHIP_VARIANTS: Partial<Record<NoteProposalType, ChipVariant>> = {
  medication_add: 'affirm',
  medication_change: 'affirm',
  medication_discontinue: 'affirm',
  hospital_event: 'caution',
  risk_change: 'caution',
  allergy_change: 'caution',
  directive_change: 'memory',
};

/**
 * The care-notice chip's accent lives outside TYPE_CHIP_VARIANTS (that
 * table is keyed by the clinical registry; SPICE types are a separate
 * set, see spiceNoteVocabulary.ts). A care notice names a fact awaiting
 * the viewer's confirm, the one thing caution is reserved for. Every
 * other SPICE category keeps the neutral chip.
 */
function chipVariantForType(type: string): ChipVariant {
  if (type === 'spice_care_notice') return 'caution';
  return TYPE_CHIP_VARIANTS[type as NoteProposalType] ?? 'neutral';
}

// Labels route through the one acronym-aware helper ("Is POA", "ER
// visit"; coverage_change displays as "Insurance change"). Field labels
// use fieldLabel; value tokens use the plain acronym-aware humanize.
const humanize = humanizeToken;

/**
 * Type to display label, derived from the type token (no hand-kept label
 * table), so it cannot drift from the registry's type names.
 *
 * The same label for every viewer: the care notice a therapist addresses
 * to "the care manager" reads "For the care manager" to that care
 * manager too. The viewer flag is carried (see the component doc) so a
 * viewer-relative reading is a one-line change here.
 */
export function proposalTypeLabel(type: string, _viewerIsCareManager = false): string {
  if (isSpiceNoteProposalType(type)) return SPICE_TYPE_LABELS[type];
  return humanize(type);
}

function isTyped(type: string): boolean {
  return TYPED_TYPES.has(type as NoteProposalType);
}

// ---------------------------------------------------------------------

/**
 * The primary and the ghost carry a transparent border the width of the
 * secondary's, so every button in a card's row is one height and the
 * primary's top is never below a neighbour's when they share a line.
 */
const ROW_BORDER: Record<CardAction['variant'], string | undefined> = {
  primary: 'border border-transparent',
  secondary: undefined,
  ghost: 'border border-transparent',
};

/**
 * A card's button row: one Button per outcome, in the order cardActions
 * gives. While `confirmDisabled`, every outcome waits and Dismiss does
 * not. The Replace button reads "Replace existing dose" when that label
 * fits the row on one line and "Replace existing" when it does not; its
 * accessible name is always the full label. The fit is measured against
 * a hidden sizer that always carries the full label with the button's own
 * classes, so the choice never flips back and forth, and it is measured
 * again whenever the row's width or the text size changes. With no layout
 * to measure (both widths 0) the full label shows.
 */
function ActionRow({
  proposalId,
  actions,
  confirmDisabled,
  onAction,
}: {
  proposalId: string;
  actions: readonly CardAction[];
  confirmDisabled: boolean;
  onAction: (action: CardAction) => void;
}): ReactNode {
  const rowRef = useRef<HTMLDivElement>(null);
  const sizerRef = useRef<HTMLButtonElement>(null);
  const fitted = actions.find((a) => a.shortLabel !== undefined);
  const fittedLabel = fitted?.label;
  const [fullFits, setFullFits] = useState(true);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const sizer = sizerRef.current;
    if (fittedLabel === undefined || row === null || sizer === null) return undefined;
    const measure = (): void => {
      setFullFits(sizer.getBoundingClientRect().width <= row.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(sizer);
    return () => observer.disconnect();
  }, [fittedLabel]);

  return (
    <div
      ref={rowRef}
      data-testid={`proposal-actions-${proposalId}`}
      className="flex flex-wrap items-center gap-2 pt-1"
    >
      {actions.map((a) => {
        const short = a.shortLabel !== undefined && !fullFits;
        return (
          <Button
            key={a.testId}
            size="sm"
            {...(a.variant === 'primary' ? {} : { variant: a.variant })}
            className={ROW_BORDER[a.variant]}
            data-testid={a.testId}
            disabled={a.kind !== 'dismiss' && confirmDisabled}
            {...(a.shortLabel !== undefined ? { 'aria-label': a.label } : {})}
            onClick={() => onAction(a)}
          >
            {short ? a.shortLabel : a.label}
          </Button>
        );
      })}
      {fitted !== undefined && (
        <span aria-hidden="true" className="pointer-events-none absolute h-0 w-0 overflow-hidden">
          <Button
            ref={sizerRef}
            size="sm"
            tabIndex={-1}
            className={`${ROW_BORDER.primary ?? ''} w-max whitespace-nowrap`}
          >
            {fitted.label}
          </Button>
        </span>
      )}
    </div>
  );
}

export interface ConfirmQueueProps {
  clientId: string;
  /** Proposals below this confidence sit in the closer-look section and
   *  are never added by the header. */
  confidenceThreshold?: number;
  /** record.vitals.viewerIsCareManager: true when the signed-in viewer
   *  is this client's assigned care manager. Carried to the label so the
   *  spice_care_notice category can read viewer-relatively; today it
   *  reads "For the care manager" for every viewer. Defaults false. */
  viewerIsCareManager?: boolean;
}

const POSTAL_PART_KEYS = ['line1', 'line2', 'city', 'state', 'zip'] as const;
type PostalPartKey = (typeof POSTAL_PART_KEYS)[number];

/** One part of a card's address as it stands: the edit, else the note's
 *  part, else empty (a note's legacy text fills no part). */
function postalPartValue(p: ProposalWire, field: string, k: PostalPartKey, edits: Readonly<Record<string, string>>): string {
  const edit = edits[`${p.id}:${field}.${k}`];
  if (edit !== undefined) return edit;
  const proposed = p.payload[field];
  if (proposed !== null && typeof proposed === 'object' && !Array.isArray(proposed)) {
    const v = (proposed as Record<string, unknown>)[k];
    return typeof v === 'string' ? v : '';
  }
  return '';
}

/** A card's edited address as the confirm sends it: the five parts, a
 *  blank part null; every part blank is no address. */
function postalFromEdits(p: ProposalWire, field: string, edits: Readonly<Record<string, string>>): Record<string, string | null> | null {
  const out: Record<string, string | null> = {};
  for (const k of POSTAL_PART_KEYS) {
    const v = postalPartValue(p, field, k, edits).trim();
    out[k] = v === '' ? null : v;
  }
  return POSTAL_PART_KEYS.every((k) => out[k] === null) ? null : out;
}

interface ResolveVars {
  proposal: ProposalWire;
  action: ProposalAction;
  /** The confirm's payload, as sent. */
  payload?: Record<string, unknown>;
  /** Sent as target_row_id: the confirm updates that row instead of
   *  inserting. Only the button that names the row carries one. */
  targetRowId?: string;
  /** A housing note's wait line, built when the confirm is sent, shown on
   *  its card if the server answers housing_place_wait. */
  housingWaitLine?: string;
}

export function ConfirmQueue({
  clientId,
  confidenceThreshold = DEFAULT_CONFIDENCE_THRESHOLD,
  viewerIsCareManager = false,
}: ConfirmQueueProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  const queryClient = useQueryClient();
  const queryKey = ['kith-proposals', tenantSlug, clientId];
  // The client's care team and pronouns for the care-team picker, from
  // the record already loaded on this page; none read here.
  const careTeamRecord = cachedClientRecord(queryClient, tenantSlug, clientId);
  // A housing note's wait and its return-home line read the client's
  // addresses and pronouns from the same record (proposalHolds.ts); with
  // no record loaded the card does not predict and the server decides.
  const housingContext =
    careTeamRecord === undefined || careTeamRecord === null
      ? undefined
      : { addresses: careTeamRecord.addresses, pronouns: careTeamRecord.vitals.pronouns ?? null };

  // Per-proposal typed-field edits, keyed `${proposalId}:${field}`.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Per-proposal care-team picks (the record form's picker on a
  // prescriber, a provider or a facility), keyed `${proposalId}:${field}`;
  // a keystroke in the box drops the pick.
  const [careTeamPicks, setCareTeamPicks] = useState<Record<string, CareTeamPick | null>>({});
  // Per-proposal Directory pick for provider_new cards, keyed by proposal
  // id; travels as the directory arm in the confirm payload.
  const [directoryLinks, setDirectoryLinks] = useState<
    Record<string, { kind: 'individual' | 'org'; id: string; name: string }>
  >({});
  // An appointment card's place picked from the Directory (0233), by
  // proposal: where it came from, and the entry's address when it has text
  // only. The pick copies the name and parts into the card's boxes.
  const [placeLinks, setPlaceLinks] = useState<
    Record<string, { type: 'individual' | 'organization'; id: string; name: string; text: string | null }>
  >({});
  const [placeSearches, setPlaceSearches] = useState<Record<string, string>>({});
  const [placeSearching, setPlaceSearching] = useState<Record<string, boolean>>({});

  // A care-team provider's office filled into an appointment card's
  // place when the row has no Directory link (LP-26), by proposal: the
  // place name Where shows as held. A row with a link holds it in
  // placeLinks instead, and its id travels.
  const [placeFromTeam, setPlaceFromTeam] = useState<Record<string, string>>({});
  // The place a provider pick offers over a place already set, by
  // proposal: "Use <place> instead" fills it; nothing fills on its own.
  const [placeOffers, setPlaceOffers] = useState<Record<string, ProviderPlace>>({});
  // The latest provider pick per proposal: a slower answer for an earlier
  // pick is dropped.
  const providerPickSeq = useRef<Record<string, number>>({});

  /** A place pick on an appointment card: the name and the entry's parts
   *  fill the card's boxes (editable); only the entry's id travels. */
  async function pickPlace(
    p: ProposalWire,
    entry: { id: string; name: string; type: 'organization' | 'individual' },
    // The address field the pick fills: an appointment's place (with its
    // name), or a housing note's address (22l2: the facility's parts only;
    // no id travels).
    target: 'location_address' | 'address' = 'location_address',
  ): Promise<void> {
    setPlaceSearching((prev) => ({ ...prev, [p.id]: false }));
    setPlaceSearches((prev) => ({ ...prev, [p.id]: '' }));
    // A failed read still records where the place came from.
    const { parts, text } = await entryAddress(tenantSlug, entry);
    if (target === 'location_address') dropTeamPlace(p);
    setPlaceLinks((prev) => ({ ...prev, [p.id]: { type: entry.type, id: entry.id, name: entry.name, text } }));
    setEdits((prev) => {
      const next = target === 'location_address' ? { ...prev, [`${p.id}:location_name`]: entry.name } : { ...prev };
      for (const k of POSTAL_PART_KEYS) next[`${p.id}:${target}.${k}`] = parts === null ? '' : parts[k];
      return next;
    });
    clearHeldError(p.id);
  }

  function dropTeamPlace(p: ProposalWire): void {
    setPlaceFromTeam((prev) => {
      if (prev[p.id] === undefined) return prev;
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
  }

  /** Drops an appointment card's Directory pick; the name and the boxes
   *  keep what they hold. */
  function clearPlace(p: ProposalWire): void {
    setPlaceLinks((prev) => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
    dropTeamPlace(p);
    setPlaceSearching((prev) => ({ ...prev, [p.id]: false }));
  }

  /** True while an appointment card's place is empty: no pick, no name
   *  and no address part, typed or from the note. A provider pick fills
   *  the place only then (never over what a person typed). */
  function placeIsEmpty(p: ProposalWire): boolean {
    if (placeLinks[p.id] !== undefined || placeFromTeam[p.id] !== undefined) return false;
    if (effective(p, edits, 'location_name').trim() !== '') return false;
    return POSTAL_PART_KEYS.every((k) => postalPartValue(p, 'location_address', k, edits).trim() === '');
  }
  // The provider's place is fetched, so the emptiness it fills on is read
  // when the answer lands, from the card as it then stands.
  const placeIsEmptyNow = useRef(placeIsEmpty);
  placeIsEmptyNow.current = placeIsEmpty;

  /** Fills an appointment card's place from a provider's place: its name
   *  and parts, and the Directory entry it links (whose id travels). */
  function takePlace(p: ProposalWire, place: ProviderPlace): void {
    setEdits((prev) => {
      const out = { ...prev, [`${p.id}:location_name`]: place.name };
      for (const k of POSTAL_PART_KEYS) out[`${p.id}:location_address.${k}`] = place.parts === null ? '' : place.parts[k];
      return out;
    });
    if (place.entry !== null) {
      const entry = place.entry;
      setPlaceLinks((prev) => ({ ...prev, [p.id]: { type: entry.type, id: entry.id, name: place.name, text: place.text } }));
      dropTeamPlace(p);
    } else {
      setPlaceLinks((prev) => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
      setPlaceFromTeam((prev) => ({ ...prev, [p.id]: place.name }));
    }
    setPlaceSearching((prev) => ({ ...prev, [p.id]: false }));
    setPlaceOffers((prev) => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
    clearHeldError(p.id);
  }

  /** A provider pick on an appointment card: its place (appointmentPlace)
   *  fills an empty place, or is offered over one already set. A care-team
   *  row with neither an address nor a Directory link, or a typed
   *  provider, offers nothing. */
  async function fillPlaceFromProvider(p: ProposalWire, next: { value: string; pick: CareTeamPick | null }): Promise<void> {
    if (p.proposal_type !== 'appointment_upcoming' && p.proposal_type !== 'appointment_past') return;
    const seq = (providerPickSeq.current[p.id] ?? 0) + 1;
    providerPickSeq.current[p.id] = seq;
    const place = next.pick === null ? null : await providerPlace(tenantSlug, next.pick, next.value, careTeamRecord?.providers ?? []);
    if (providerPickSeq.current[p.id] !== seq) return;
    if (place !== null && placeIsEmptyNow.current(p)) {
      takePlace(p, place);
      return;
    }
    setPlaceOffers((prev) => {
      const out = { ...prev };
      if (place === null) delete out[p.id];
      else out[p.id] = place;
      return out;
    });
  }
  // The Directory search unmounts on a pick, and on Escape from an empty
  // box, so focus would fall to the page. The control that takes its
  // place is focused once it has rendered: Unlink after a pick, the
  // opener after Escape. Keyed `${control}:${proposalId}`.
  const directoryControls = useRef<Record<string, HTMLButtonElement | null>>({});
  const [directoryFocus, setDirectoryFocus] = useState<string | null>(null);
  useEffect(() => {
    if (directoryFocus === null) return;
    directoryControls.current[directoryFocus]?.focus();
    setDirectoryFocus(null);
  }, [directoryFocus]);
  // The email sentence a contact card shows after its box is left, keyed
  // `${proposalId}:${field}`. Advisory: the confirm never waits on it.
  const [emailProblems, setEmailProblems] = useState<Record<string, string | null>>({});
  const [resolveError, setResolveError] = useState<unknown>(null);
  // After a confirm leaves the queue, the remaining cards settle up into
  // place (a CSS animation, inert under reduced motion). The tick re-keys
  // the list wrapper so the animation replays per confirm; edits live in
  // this component's state, so the remount is purely visual. A dismiss is
  // a quiet no and does not settle.
  const [settleTick, setSettleTick] = useState(0);
  // Which cards the care manager has toggled with Adjust or Done. A card's
  // editor starts closed, or open for a held card, and each toggle flips
  // it. Presentation only: edits travel on confirm either way.
  const [adjustToggled, setAdjustToggled] = useState<ReadonlySet<string>>(new Set());
  // The line a held card shows after the server refused its confirm,
  // keyed by proposal id; cleared when that card's edits change or its
  // next resolve is sent.
  const [heldErrors, setHeldErrors] = useState<Readonly<Record<string, string>>>({});
  // Resolves sent and not yet settled. The list is read again only when
  // this returns to zero, so a card mid-resolve never reappears.
  const inFlight = useRef(0);

  const { data, isPending, isError, error, refetch } = useQuery<ProposalsResponse>({
    queryKey,
    queryFn: ({ signal }) => notesApi.listProposals(tenantSlug, clientId, 'open', signal),
  });

  /**
   * The confirm-time payload: the proposal's payload with the care
   * manager's typed-field edits merged over it (typed types only; other
   * types travel as they are). A proposed row keeps its other payload keys
   * untouched; a held row is cleaned first, so it sends only its editor's
   * fields. An edit to a field that does not apply to the card as it
   * stands stays home: the payload's own value rides untouched.
   */
  function editedPayload(p: ProposalWire): Record<string, unknown> {
    const spec = specForType(p.proposal_type);
    if (!spec || !isTyped(p.proposal_type)) return p.payload;
    const next = { ...p.payload };
    const projection = recordProjectionForType(p.proposal_type);
    const read = (f: string): string => effective(p, edits, f) || cardDefault(p.proposal_type, f);
    for (const [field, fs] of editableFields(spec)) {
      if (!appliesOnCard(p, edits, field, fs)) {
        // A value the record form hides for the card's values travels as
        // the form sends it (an outcome as null, a repeat as 'none'); a
        // field with nothing in it stays absent. One the registry's own
        // rule hides, or a direct-entry field, keeps the payload's value
        // untouched.
        const carried = p.payload[field];
        const hasValue = carried !== null && carried !== undefined && !(typeof carried === 'string' && carried.trim() === '');
        if (hasValue && projection !== undefined && fs.directOnly !== true && fieldApplies(fs, read)) {
          const hidden = browserValueWhenHidden(projection, field);
          if (hidden !== undefined) next[field] = hidden;
        }
        continue;
      }
      if (fs.kind === 'postal_address') {
        // The five parts travel as one object once any was changed; an
        // untouched address keeps the note's value (parts or text).
        const touched = POSTAL_PART_KEYS.some((k) => edits[`${p.id}:${field}.${k}`] !== undefined);
        if (touched) next[field] = postalFromEdits(p, field, edits);
        continue;
      }
      const v = edits[`${p.id}:${field}`];
      if (v === undefined) continue;
      if (fs.kind === 'boolean') {
        next[field] = v === 'true';
      } else if (fs.kind === 'enum_set') {
        // Ticks travel as the set; every tick emptied travels as null.
        const tokens = v.split(',').filter(Boolean);
        next[field] = tokens.length === 0 ? null : tokens;
      } else {
        // A cleared field travels as null: the per-type schema rejects
        // empty strings and accepts null.
        // A typed phone travels as the record form sends it.
        next[field] = v.trim() === '' ? null : fs.kind === 'string' && fs.textKind === 'phone' ? normalizePhone(v) : v;
      }
    }
    // A care-team pick travels beside its text as exactly one id field,
    // as the record form sends it: a care-team row, else a Directory
    // person or organization. A hospital stay keeps the organization
    // alone, so a care-team row sends the organization it links, if any.
    if (projection !== undefined) {
      for (const [field] of formFields(spec)) {
        const entry = careTeamFieldOf(projection, field);
        const pick = careTeamPicks[`${p.id}:${field}`];
        if (entry === undefined || pick === undefined || pick === null) continue;
        if (pick.kind === 'care_team') {
          if (entry.providerIdField !== undefined) {
            next[entry.providerIdField] = pick.providerId;
          } else {
            const row = (careTeamRecord?.providers ?? []).find((r) => r.id === pick.providerId);
            if (row?.link.kind === 'org') next[entry.orgField] = row.link.id;
          }
        } else if (pick.type === 'individual') {
          if (entry.individualField !== undefined) next[entry.individualField] = pick.id;
        } else {
          next[entry.orgField] = pick.id;
        }
      }
    }
    // provider_new: the Directory pick rides the directory arm (the
    // provider writer checks it exists, is in this tenant, and is one arm).
    // The note's specialty and organization ride as the note said them;
    // the writer stores neither per client, and the card's "The note also
    // says" line adds nothing to the wire.
    if (p.proposal_type === 'provider_new') {
      const pick = directoryLinks[p.id];
      if (pick !== undefined) {
        next[pick.kind === 'org' ? 'directory_org_id' : 'directory_individual_id'] = pick.id;
      }
    }
    // An appointment's place picked from the Directory: where it came
    // from rides beside the copied name and parts (the writer checks it).
    const place = placeLinks[p.id];
    if (place !== undefined && (p.proposal_type === 'appointment_upcoming' || p.proposal_type === 'appointment_past')) {
      next[place.type === 'individual' ? 'location_directory_individual_id' : 'location_directory_org_id'] = place.id;
    }
    return next;
  }

  const resolveMut = useMutation({
    mutationFn: ({ proposal, action, payload, targetRowId }: ResolveVars) =>
      notesApi.resolveProposal(tenantSlug, proposal.id, {
        action,
        ...(action === 'confirmed' ? { payload } : {}),
        ...(action === 'confirmed' && targetRowId !== undefined
          ? { target_row_id: targetRowId }
          : {}),
      }),
    onMutate: async ({ proposal, action }) => {
      // Optimistic removal; the list's order is kept so a failed card can
      // go back to its own place.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ProposalsResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<ProposalsResponse>(queryKey, {
          proposals: previous.proposals.filter((p) => p.id !== proposal.id),
        });
      }
      setResolveError(null);
      // The optimistic removal and the settle are the same beat.
      if (action === 'confirmed') setSettleTick((t) => t + 1);
      return {
        order: (previous?.proposals ?? []).map((p) => p.id),
        row: previous?.proposals.find((p) => p.id === proposal.id),
      };
    },
    onError: (err, { proposal, action, payload, housingWaitLine }, ctx) => {
      // Only the failed card comes back, at its place in the list; cards
      // other resolves took off stay off.
      const order = ctx?.order ?? [];
      const rank = (id: string): number => {
        const i = order.indexOf(id);
        return i === -1 ? order.length : i;
      };
      const current = queryClient.getQueryData<ProposalsResponse>(queryKey);
      const list = current?.proposals ?? [];
      if (!list.some((p) => p.id === proposal.id)) {
        queryClient.setQueryData<ProposalsResponse>(queryKey, {
          proposals: [...list, ctx?.row ?? proposal].sort((a, b) => rank(a.id) - rank(b.id)),
        });
      }
      // A held confirm the server refuses names the field on its card,
      // never in the banner.
      if (
        action === 'confirmed' &&
        isHeldForReview(proposal) &&
        err instanceof ApiError &&
        err.status === 400
      ) {
        const line = heldFailureLine(proposal, payload ?? {}, err);
        setHeldErrors((prev) => ({ ...prev, [proposal.id]: line }));
        return;
      }
      // A housing note the record cannot take as it stands (the client's
      // addresses changed since the card was drawn): its wait, on its card.
      if (action === 'confirmed' && err instanceof ApiError && err.status === 409 && errorCodeOf(err) === 'housing_place_wait') {
        setHeldErrors((prev) => ({ ...prev, [proposal.id]: housingWaitLine ?? '' }));
        return;
      }
      // A seat that reads the record without keeping it (the server's
      // care_record_read_only): the server's own sentence, on its card.
      const said = serverSentence(err, 'record_edits');
      if (said !== null) {
        setHeldErrors((prev) => ({ ...prev, [proposal.id]: said }));
        return;
      }
      setResolveError(err);
    },
    onSuccess: (_res, { proposal, action }) => {
      // A confirm commits its record_event server-side before this
      // resolves, but the record cards read their own ['clientRecord']
      // query; refetch it so the change lands in its tab without a page
      // reload. A dismiss writes nothing to the record and needs no refetch.
      if (action === 'confirmed') {
        void queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
        // Announce the landing so the destination record card, where
        // mounted, pulses once (cardSpine listens).
        const cardTestId = proposalDestinationTestId(proposal.proposal_type);
        if (cardTestId !== null) {
          window.dispatchEvent(
            new CustomEvent<ConfirmLandedDetail>(CONFIRM_LANDED_EVENT, {
              detail: { cardTestId },
            }),
          );
        }
      }
    },
    onSettled: () => {
      inFlight.current -= 1;
      if (inFlight.current === 0) {
        void queryClient.invalidateQueries({ queryKey });
        // Your day counts open updates too: its read goes stale so the
        // count follows when it is next shown.
        void queryClient.invalidateQueries({ queryKey: ['cm-home', tenantSlug] });
      }
    },
  });

  function clearHeldError(id: string): void {
    setHeldErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  /** Send one resolve, counted in flight from the moment it is sent. A
   *  confirm carries its payload as it stands now. */
  function resolve(vars: ResolveVars): void {
    inFlight.current += 1;
    clearHeldError(vars.proposal.id);
    resolveMut.mutate(
      vars.action === 'confirmed'
        ? { ...vars, payload: editedPayload(vars.proposal), ...housingWaitVar(vars.proposal) }
        : vars,
    );
  }

  /** A housing note's wait line for its variables: the card's own copy of
   *  the rule names it; with no record loaded to read, the move is the
   *  likelier reason. */
  function housingWaitVar(p: ProposalWire): { housingWaitLine?: string } {
    if (p.proposal_type !== 'housing_change') return {};
    return {
      housingWaitLine:
        proposalHold(p, edits, housingContext)?.message ??
        housingWaitMessage('moved', careTeamRecord?.vitals.pronouns ?? null),
    };
  }

  // Held rows are cleaned once, here; the set-aside line reads the stored
  // row, everything else the cleaned one.
  const rawProposals = data?.proposals ?? [];
  const proposals = rawProposals.map(cleanHeldProposal);
  const high = proposals.filter((p) => p.confidence >= confidenceThreshold);
  const low = proposals.filter((p) => p.confidence < confidenceThreshold);

  /**
   * A schedule change (spice_status_change) pauses the client's visits,
   * which needs manage_client_identity (canEditClientSchedule). For anyone
   * else the server refuses both its confirm and its dismiss, so the card
   * offers neither and the proposal stays open for someone who may change
   * the schedule. A clinical proposal (any but SPICE's) changes the
   * record, so it resolves only for a seat that keeps it
   * (canWriteClinicalRecord), the server refusing both actions otherwise
   * (LP-48). A demo-only author is left to the demo fence, as on the
   * server. Every other proposal resolves.
   */
  const keepsRecord = canWriteClinicalRecord(whoami) || !canWriteClinicalNotes(whoami);
  function canResolve(p: ProposalWire): boolean {
    if (!isSpiceNoteProposalType(p.proposal_type) && !keepsRecord) return false;
    return p.proposal_type !== 'spice_status_change' || canEditClientSchedule(whoami);
  }
  /** Only the office chooses who receives the bill: a viewer without
   *  set_bill_to gets no Billing Contact checkbox, and a contact card
   *  the note calls the billing contact says the office sets it. */
  const mayChooseBillTo = canSetBillTo(whoami);
  /** A proposal the record would refuse as it stands (an appointment with
   *  no real date yet): its primary waits for the fix under Adjust, and
   *  the header leaves it. */
  function holdFor(p: ProposalWire): ReturnType<typeof proposalHold> {
    return proposalHold(p, edits, housingContext);
  }

  const bulk = bulkAction({ proposals, threshold: confidenceThreshold, canResolve, holdFor });

  /** The header's click: each card it adds sends its own primary request,
   *  row target included. */
  function addAll(): void {
    for (const p of bulk.actOn) {
      const main = cardActions(p, edits)[0];
      resolve({ proposal: p, action: 'confirmed', targetRowId: main?.targetRowId });
    }
  }

  function onCardAction(p: ProposalWire, a: CardAction): void {
    if (a.kind === 'dismiss') {
      resolve({ proposal: p, action: 'dismissed' });
      return;
    }
    resolve({ proposal: p, action: 'confirmed', targetRowId: a.targetRowId });
  }

  /** The typed-field editor for a proposal: one control per registry
   * field that applies, in one column, in registry order (directory-link
   * uuids and display-only fields such as date_text are left out;
   * date_text shows among the facts and rides the wire, but is never
   * typed). A field that applies only with another field's value (the
   * code-status value, with the instrument) shows only while the card's
   * values, edits first, say it applies. */
  function renderTypedFields(p: ProposalWire, spec: ProposalPayloadSpec): ReactNode {
    const projection = recordProjectionForType(p.proposal_type);
    const read = (f: string): string => effective(p, edits, f) || cardDefault(p.proposal_type, f);
    // Billing contact draws for every seat, as on the record form: a seat
    // without set_bill_to reads the office line there, with no box.
    const shown = formFields(spec).filter(([field, fs]) => appliesOnCard(p, edits, field, fs));
    // The record form's order where it has one; any other field after.
    const order = projection === undefined ? undefined : RECORD_FIELD_ORDER[projection];
    const rank = (field: string): number => {
      const i = order?.indexOf(field) ?? -1;
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const fields = shown
      .map((entry, i) => ({ entry, i }))
      .sort((a, b) => rank(a.entry[0]) - rank(b.entry[0]) || a.i - b.i)
      .map(({ entry }) => entry);
    return (
      <div className="grid grid-cols-1 gap-2 pt-1">
        {fields.map(([field, fs]) => {
          const id = `prop-${p.id}-${field}`;
          const testId = `proposal-${p.id}-${field}`;
          const key = `${p.id}:${field}`;
          const stored = edits[key];
          const label = cardFieldLabel(p.proposal_type, field);
          const required =
            fs.required === true || (projection !== undefined && requiredWhen(projection, field, read));
          const set = (val: string): void => {
            setEdits((prev) => ({ ...prev, [key]: val }));
            clearHeldError(p.id);
          };

          const isAppointment = p.proposal_type === 'appointment_upcoming' || p.proposal_type === 'appointment_past';
          // An appointment's place (0233) is one Where block: the name
          // line (a Directory name box) and the address boxes, drawn at
          // the name's slot; the address field has no block of its own.
          if (isAppointment && field === 'location_address') return null;
          if (isAppointment && field === 'location_name') {
            const noteWords = p.payload['location'];
            const legacy = typeof noteWords === 'string' && noteWords.trim() !== '' ? noteWords : null;
            const placeLink = placeLinks[p.id];
            const teamPlace = placeFromTeam[p.id];
            const placeName = stored ?? scalar(p.payload[field]);
            const offer = placeOffers[p.id];
            const offered =
              offer !== undefined &&
              !holdsPlace({ name: placeName, entry: placeLink === undefined ? null : placeLink }, offer);
            const part = (k: PostalPartKey): string => postalPartValue(p, 'location_address', k, edits);
            const setPart = (k: PostalPartKey) => (val: string): void => {
              setEdits((prev) => ({ ...prev, [`${p.id}:location_address.${k}`]: val }));
              clearHeldError(p.id);
            };
            return (
              <AppointmentWhere
                key={field}
                surface="card"
                nameId={id}
                nameTestId={testId}
                addressIdPrefix={`proposal-${p.id}-address`}
                addressTestId={`proposal-${p.id}-location_address`}
                name={placeName}
                onNameChange={(v) => {
                  set(v);
                  if (placeLink !== undefined || teamPlace !== undefined) clearPlace(p);
                }}
                onPick={(entry) => void pickPlace(p, entry, 'location_address')}
                link={
                  placeLink !== undefined
                    ? { name: placeLink.name }
                    : teamPlace !== undefined
                      ? { name: teamPlace, from: 'care_team' }
                      : null
                }
                offer={offered ? { name: offer.name, onUse: () => takePlace(p, offer) } : null}
                searching={placeSearching[p.id] === true}
                onChange={() => setPlaceSearching((prev) => ({ ...prev, [p.id]: true }))}
                onClear={() => clearPlace(p)}
                directoryText={placeLink?.text ?? null}
                above={
                  legacy !== null ? (
                    <p data-testid="appointment-location-from-note" className="text-xs text-sage-800">
                      From the note: {legacy}
                    </p>
                  ) : undefined
                }
                address={{
                  values: { line1: part('line1'), line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') },
                  onLine1Change: setPart('line1'),
                  onLine2Change: setPart('line2'),
                  onCityChange: setPart('city'),
                  onStateChange: setPart('state'),
                  onZipChange: setPart('zip'),
                }}
              />
            );
          }

          if (fs.kind === 'postal_address') {
            // The record form's five fields. A confirm records what the
            // note said, so no address check runs here; a note's legacy
            // text shows above empty fields.
            // 22l2: a housing note's address offers the Directory search for
            // the facility (organizations), filling the boxes from its parts.
            const isFacility = p.proposal_type === 'housing_change' && field === 'address';
            const searchId = `proposal-${p.id}-facility-directory`;
            const proposed = p.payload[field];
            const legacy = typeof proposed === 'string' && proposed.trim() !== '' ? proposed : null;
            const placeLink = isFacility ? placeLinks[p.id] : undefined;
            const part = (k: PostalPartKey): string => postalPartValue(p, field, k, edits);
            const setPart = (k: PostalPartKey) => (val: string): void => {
              setEdits((prev) => ({ ...prev, [`${p.id}:${field}.${k}`]: val }));
              clearHeldError(p.id);
            };
            return (
              <fieldset key={field} data-testid={testId} className="flex min-w-0 flex-col gap-2">
                <legend className="text-xs text-ink-soft">{label}</legend>
                {legacy !== null && (
                  <p data-testid="address-from-note-text" className="text-xs text-sage-800">
                    From the note: {legacy}
                  </p>
                )}
                {isFacility &&
                  (placeLink !== undefined && placeSearching[p.id] !== true ? (
                    <div
                      data-testid={`proposal-${p.id}-facility-picked`}
                      className="flex flex-wrap items-center gap-x-2 text-xs text-sage-800"
                    >
                      <span>
                        From the Directory: <span className="font-semibold">{placeLink.name}</span>
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setPlaceSearching((prev) => ({ ...prev, [p.id]: true }))}>
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Field label="Find the facility in the Directory" htmlFor={searchId}>
                      <DirectoryNameBox
                        id={searchId}
                        testId={searchId}
                        value={placeSearches[p.id] ?? ''}
                        onChange={(v) => setPlaceSearches((prev) => ({ ...prev, [p.id]: v }))}
                        onPick={(entry) => void pickPlace(p, entry, 'address')}
                        only="organization"
                        listLabel="Directory facilities"
                        linked={false}
                        boxAria={fieldBoxAria(searchId, {})}
                      />
                    </Field>
                  ))}
                {placeLink?.text != null && (
                  <p className="text-xs text-sage-800">From the Directory: {placeLink.text}</p>
                )}
                <AddressFields
                  idPrefix={`proposal-${p.id}-address`}
                  values={{ line1: part('line1'), line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') }}
                  onLine1Change={setPart('line1')}
                  onLine2Change={setPart('line2')}
                  onCityChange={setPart('city')}
                  onStateChange={setPart('state')}
                  onZipChange={setPart('zip')}
                />
              </fieldset>
            );
          }

          if (fs.kind === 'enum') {
            // Opens on the proposal's value, else the value the writer
            // would take, else the record form's own empty state.
            const current = stored ?? (scalar(p.payload[field]) || cardDefault(p.proposal_type, field));
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <SelectField size="default"
                  id={id}
                  data-testid={testId}
                  className="w-full"
                  value={current}
                  onChange={(e) => set(e.target.value)}
                >
                  {fs.defaultValue === undefined && (
                    <option value="">{(projection && selectOpening(projection, field)) ?? '…'}</option>
                  )}
                  {(fs.values ?? []).map((v) => (
                    <option key={v} value={v}>
                      {cardValueLabel(p.proposal_type, field, v)}
                    </option>
                  ))}
                </SelectField>
              </Field>
            );
          }

          if (fs.kind === 'enum_set') {
            // The record form's ticks; the state is the comma-joined set
            // in the vocabulary's order, which is how it travels.
            return (
              <TickGroup
                key={field}
                id={id}
                legend={label}
                options={((fs.values ?? []) as readonly string[]).map((v) => ({
                  value: v,
                  label: cardValueLabel(p.proposal_type, field, v),
                }))}
                value={(stored ?? read(field)).split(',').filter(Boolean)}
                onChange={(next) => set(next.join(','))}
                testIdPrefix={testId}
              />
            );
          }

          if (fs.kind === 'boolean') {
            // The record form's flag row, Billing contact's office line included.
            const checked = stored !== undefined ? stored === 'true' : p.payload[field] === true;
            return (
              <Fragment key={field}>
                <CheckboxRow
                  id={id}
                  testId={testId}
                  label={label}
                  checked={checked}
                  onChange={(next) => set(next ? 'true' : 'false')}
                  billing={field === 'is_billing'}
                  mayChooseBillTo={mayChooseBillTo}
                />
                {/* Its own line across the card, only while ticked. */}
                {field === 'is_billing' && mayChooseBillTo && checked && <BillingHint className="col-span-full" />}
              </Fragment>
            );
          }

          if (fs.kind === 'risk_category') {
            const current = stored ?? scalar(p.payload[field]);
            // The card offers the platform default risk categories and
            // adds the proposal's current value when it is not among them,
            // so a tenant's own category still prefills. The server checks
            // the category against the tenant's vocabulary at extraction
            // and at confirm, so a default the tenant dropped is refused
            // at confirm, never written.
            const options =
              current && !DEFAULT_RISK_CATEGORIES.includes(current)
                ? [...DEFAULT_RISK_CATEGORIES, current]
                : DEFAULT_RISK_CATEGORIES;
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <SelectField size="default"
                  id={id}
                  data-testid={testId}
                  className="w-full"
                  value={current}
                  onChange={(e) => set(e.target.value)}
                >
                  <option value="">…</option>
                  {options.map((v) => (
                    <option key={v} value={v}>
                      {humanize(v)}
                    </option>
                  ))}
                </SelectField>
              </Field>
            );
          }

          if (fs.kind === 'email') {
            // An email box, never capitalised. Leaving a box the person
            // typed in trims and lower-cases it; an untouched box keeps
            // the note's words, which the writer lower-cases. A shape
            // problem shows its sentence after leaving and is advisory:
            // the confirm stays enabled and sends the text lower-cased.
            const current = stored ?? scalar(p.payload[field]);
            const problem = emailProblems[key] ?? null;
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <TextInput
                  id={id}
                  data-testid={testId}
                  type="email"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoComplete="off"
                  className={`${FLD} h-9 px-2 !text-sm`}
                  value={current}
                  onChange={(e) => {
                    setEmailProblems((prev) => ({ ...prev, [key]: null }));
                    set(e.target.value);
                  }}
                  onBlur={() => {
                    if (stored === undefined) return;
                    const typed = stored.trim().toLowerCase();
                    set(typed);
                    setEmailProblems((prev) => ({ ...prev, [key]: contactEmailProblem(typed) }));
                  }}
                />
                {problem !== null && (
                  <p data-testid={`${testId}-problem`} className="mt-1 text-xs text-pepper">
                    {problem}
                  </p>
                )}
              </Field>
            );
          }

          // The record form's care-team picker: the client's care team
          // first, then the Directory; typing a name stays possible.
          const careTeam = fs.kind === 'string' && projection !== undefined ? careTeamFieldOf(projection, field) : undefined;
          if (careTeam !== undefined) {
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <CareTeamCombobox
                  id={id}
                  value={stored ?? scalar(p.payload[field])}
                  pick={careTeamPicks[key] ?? null}
                  onChange={(next) => {
                    setCareTeamPicks((prev) => ({ ...prev, [key]: next.pick }));
                    set(next.value);
                    if (field === 'provider') void fillPlaceFromProvider(p, next);
                  }}
                  careTeam={careTeamRecord?.providers ?? []}
                  pronouns={careTeamRecord?.vitals.pronouns ?? null}
                  listLabel={careTeam.listLabel}
                  only={careTeam.only}
                  nameKind={careTeam.nameKind}
                  testIdPrefix={testId}
                  required={required}
                />
              </Field>
            );
          }

          // A field of many lines is the record form's growing box: a typed
          // address, a clinical note.
          if (fs.kind === 'string' && fs.multiline === true) {
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <TextArea
                  id={id}
                  data-testid={testId}
                  grow
                  rows={2}
                  className={FLD_TEXTAREA_GROW}
                  value={stored ?? scalar(p.payload[field])}
                  onChange={(e) => set(e.target.value)}
                  {...textBoxProps(fs.textKind)}
                />
              </Field>
            );
          }

          // A phone formats as typed, as the record form's does; an
          // untouched box shows the note's number formatted and travels as
          // the note said it.
          if (fs.kind === 'string' && fs.textKind === 'phone') {
            return (
              <Field key={field} label={label} required={required} htmlFor={id}>
                <TextInput
                  id={id}
                  data-testid={testId}
                  type="tel"
                  className={`${FLD} h-9 px-2 !text-sm`}
                  value={stored ?? formatPhoneDisplay(scalar(p.payload[field]))}
                  onChange={(e) => set(formatPhoneInput(e.target.value))}
                />
              </Field>
            );
          }

          // A date or a string. A name kind takes its name case on leaving
          // the box; every other value is kept as typed. An untouched box
          // travels as the note said it, and the server cases a name. A
          // date the record form keeps in the past caps at today.
          const current = stored ?? scalar(p.payload[field]);
          const pastOnly = fs.kind === 'date' && projection !== undefined && isPastByNature(projection, field);
          return (
            <Field key={field} label={label} required={required} htmlFor={id}>
              {fs.kind === 'date' ? (
                <DateInput
                  id={id}
                  data-testid={testId}
                  size="default"
                  className="w-full"
                  value={current}
                  onChange={(e) => set(e.target.value)}
                  {...(pastOnly ? { max: practiceTodayIso() } : {})}
                />
              ) : (
                <TextInput
                  id={id}
                  data-testid={testId}
                  type={fs.kind === 'time' ? 'time' : 'text'}
                  className={`${FLD} h-9 px-2 !text-sm`}
                  value={current}
                  onChange={(e) => set(e.target.value)}
                  {...(fs.kind === 'string' ? textBoxProps(fs.textKind) : {})}
                />
              )}
            </Field>
          );
        })}
      </div>
    );
  }

  // Which provider_new card has its Directory search open (one at a time;
  // the search autofocuses, so it is opened by the user, never on load).
  const [openDirectorySearch, setOpenDirectorySearch] = useState<string | null>(null);

  /** The provider_new card's confirm-time Directory link: once picked, the
   *  linked profile and Unlink; otherwise a quiet opener for the house
   *  DirectorySearch. A pick sets the directory arm and nothing else: no
   *  typed field is filled and no Directory record is read. */
  function renderDirectoryLink(p: ProposalWire): ReactNode {
    const pick = directoryLinks[p.id];
    if (pick !== undefined) {
      return (
        <div
          data-testid={`proposal-directory-linked-${p.id}`}
          className="flex flex-wrap items-center gap-2 rounded-base border border-sage-200 bg-sage-50/60 px-2.5 py-1.5 text-sm"
        >
          <span className="rounded-pill bg-sage-50 px-1.5 py-0.5 text-label font-bold uppercase tracking-wide text-sage-400">
            {pick.kind === 'org' ? 'Org' : 'Person'}
          </span>
          <span className="min-w-0 font-semibold text-sage-800 [overflow-wrap:anywhere]">{pick.name}</span>
          <Button
            ref={(el) => {
              directoryControls.current[`unlink:${p.id}`] = el;
            }}
            variant="ghost"
            size="sm"
            className="ml-auto"
            data-testid={`proposal-directory-unlink-${p.id}`}
            onClick={() =>
              setDirectoryLinks((prev) => {
                const next = { ...prev };
                delete next[p.id];
                return next;
              })
            }
          >
            Unlink
          </Button>
        </div>
      );
    }
    if (openDirectorySearch !== p.id) {
      return (
        <Button
          ref={(el) => {
            directoryControls.current[`open:${p.id}`] = el;
          }}
          variant="ghost"
          size="sm"
          data-testid={`proposal-directory-open-${p.id}`}
          onClick={() => setOpenDirectorySearch(p.id)}
        >
          Link a Directory profile
        </Button>
      );
    }
    return (
      <div>
        <p className="mb-1 text-xs text-ink-soft">
          Link a Directory profile so they join the care team with their
          full record. Not there yet? Confirm by name and link later from
          the care team.
        </p>
        <DirectorySearch
          onPick={(row) => {
            setDirectoryLinks((prev) => ({
              ...prev,
              [p.id]: {
                kind: row.type === 'organization' ? 'org' : 'individual',
                id: row.id,
                name: row.display_name,
              },
            }));
            setOpenDirectorySearch(null);
            setDirectoryFocus(`unlink:${p.id}`);
          }}
          onEscapeEmpty={() => {
            setOpenDirectorySearch(null);
            setDirectoryFocus(`open:${p.id}`);
          }}
        />
      </div>
    );
  }

  function toggleAdjust(id: string): void {
    setAdjustToggled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * The card's facts, one labelled line each: the label in the house label
   * role, the value in the value role beside it, dropping under it when
   * both do not fit. A medication or condition already on the record, and
   * any look-alike, are facts too ("On record", "Did you mean",
   * "Similar"). No fact is truncated. A card with no fact renders no list.
   */
  function renderFacts(p: ProposalWire): ReactNode {
    const facts = cardFacts(p, edits);
    if (facts.length === 0) return null;
    return (
      <dl data-testid={`proposal-fact-${p.id}`} className="grid gap-1">
        {facts.map((f) => (
          <div
            key={f.key}
            data-testid={`proposal-fact-${p.id}-${f.key}`}
            className="flex flex-wrap items-baseline gap-x-3"
          >
            <dt className="shrink-0 min-w-[9.5em] text-label tracking-label font-semibold uppercase text-sage-400">
              {f.label}
            </dt>
            <dd className="m-0 min-w-0 flex-1 basis-[9em] text-value font-medium text-sage-800 [overflow-wrap:anywhere]">
              {f.value}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  /** A new provider's specialty and organization, which the note gave and
   *  nothing stores per client (they come from a linked Directory entry):
   *  shown so the care manager can pick the right profile, never sent. */
  function renderAlsoSays(p: ProposalWire): ReactNode {
    const text = (k: string): string => (typeof p.payload[k] === 'string' ? (p.payload[k] as string).trim() : '');
    const parts: string[] = [];
    if (text('specialty') !== '') parts.push(`Specialty: ${toNameCase(text('specialty'), 'role')}.`);
    if (text('organization') !== '') parts.push(`Organization: ${toNameCase(text('organization'), 'place')}.`);
    if (parts.length === 0) return null;
    return (
      <p data-testid="confirm-provider-also-says" className="pt-1 text-xs text-ink-soft">
        The note also says: {parts.join(' ')}
      </p>
    );
  }

  /** A housing note's lines: the return home, and the note's own words
   *  (a facility's name), which go on the card only, never the record. */
  function renderHousingLines(p: ProposalWire): ReactNode {
    const back = housingContext === undefined ? null : housingConfirmLine(p, housingContext, edits);
    const added = housingContext === undefined ? null : housingNewPlaceLine(p, housingContext, edits);
    const note = typeof p.payload['note'] === 'string' ? p.payload['note'].trim() : '';
    return (
      <>
        {back !== null && (
          <p data-testid={`proposal-housing-back-${p.id}`} className="pt-1 text-xs text-sage-800">
            {back}
          </p>
        )}
        {added !== null && (
          <p data-testid={`proposal-housing-new-${p.id}`} className="pt-1 text-xs text-sage-800">
            {added}
          </p>
        )}
        {note !== '' && (
          <p data-testid={`proposal-housing-note-${p.id}`} className="pt-1 text-xs text-sage-800">
            The note also says: {note}
          </p>
        )}
      </>
    );
  }

  function renderCard(p: ProposalWire): ReactNode {
    const spec = specForType(p.proposal_type);
    const typed = spec !== undefined && isTyped(p.proposal_type);
    const resolvable = canResolve(p);
    const held = isHeldForReview(p) && resolvable;
    const adjusting = adjustToggled.has(p.id) !== (held && typed);
    const hold = holdFor(p);
    const missingLine = heldMissingLine(p, edits);
    const rawRow = rawProposals.find((r) => r.id === p.id) ?? p;
    const setAside = held ? setAsideLine(setAsideOf(rawRow)) : null;
    const failure = heldErrors[p.id];
    return (
      <li
        key={p.id}
        data-testid={`proposal-card-${p.id}`}
        className="bg-paper rounded-card shadow-sh1 border border-sage-800/[8%] p-4 space-y-2.5"
      >
        <div className="flex flex-wrap items-center gap-2">
          {/* No confidence score on the card: the high and closer-look
              sections carry that signal, and the quote is the trust anchor. */}
          <Chip variant={chipVariantForType(p.proposal_type)}>
            {proposalTypeLabel(p.proposal_type, viewerIsCareManager)}
          </Chip>
          {/* Adjust edits what a confirm sends: a card this viewer cannot
              resolve has nothing to send, so no Adjust. */}
          {typed && resolvable && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto !px-2 !min-h-0 text-xs"
              aria-expanded={adjusting}
              data-testid={`proposal-adjust-${p.id}`}
              onClick={() => toggleAdjust(p.id)}
            >
              {adjusting ? 'Done' : 'Adjust'}
            </Button>
          )}
        </div>

        {/* Their words as a quiet quotation, the receipt that Kith read
            the note, beside the facts they became. */}
        <figure data-testid={`proposal-quote-${p.id}`} className="border-l-2 border-warm-400/70 pl-3">
          <blockquote className="font-display italic text-value leading-snug text-ink">
            &ldquo;{p.source_quote}&rdquo;
          </blockquote>
          <figcaption className="mt-0.5 text-label text-ink-soft">from your note</figcaption>
        </figure>

        {held && (
          <p data-testid={`proposal-held-${p.id}`} className="pt-1 text-xs text-pepper">
            {heldSentence(p)}
          </p>
        )}

        {renderFacts(p)}
        {p.proposal_type === 'provider_new' && renderAlsoSays(p)}

        {billToLeftToOffice(p, edits, mayChooseBillTo) && (
          <p data-testid={`proposal-bill-to-office-${p.id}`} className="pt-1 text-xs text-ink-soft">
            The office sets who receives the bill.
          </p>
        )}

        {setAside !== null && (
          <p
            data-testid={`proposal-set-aside-${p.id}`}
            className="text-xs text-ink-soft [overflow-wrap:anywhere]"
          >
            <span className="font-semibold">{SET_ASIDE_LABEL}</span>{' '}
            {setAside.slice(SET_ASIDE_LABEL.length + 1)}
          </p>
        )}

        {typed && spec !== undefined && adjusting && renderTypedFields(p, spec)}

        {p.proposal_type === 'provider_new' && (
          <div className="pt-1">{renderDirectoryLink(p)}</div>
        )}

        {p.proposal_type === 'housing_change' && renderHousingLines(p)}

        {/* One waiting line: a missing required field, else the date. */}
        {missingLine !== null ? (
          <p data-testid={`proposal-held-missing-${p.id}`} className="pt-1 text-xs text-pepper">
            {missingLine}
          </p>
        ) : (
          hold !== null && (
            <p data-testid={`proposal-hold-${p.id}`} className="pt-1 text-xs text-pepper">
              {hold.message}
            </p>
          )
        )}

        {failure !== undefined && (
          <p data-testid={`proposal-held-error-${p.id}`} className="pt-1 text-xs text-pepper">
            {failure}
          </p>
        )}

        {resolvable ? (
          <ActionRow
            proposalId={p.id}
            actions={cardActions(p, edits)}
            confirmDisabled={hold !== null || heldMissing(p, edits).length > 0}
            onAction={(a) => onCardAction(p, a)}
          />
        ) : (
          <p data-testid={`proposal-office-only-${p.id}`} className="pt-1 text-xs text-ink-soft">
            {p.proposal_type === 'spice_status_change' ? 'The office updates the schedule.' : 'The office adds this to the record.'}
          </p>
        )}
      </li>
    );
  }

  return (
    <section data-testid="confirm-queue" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 className="font-display text-xl text-sage-800">
          To confirm
          {proposals.length > 0 && (
            <span className="ml-2 text-sm text-ink-soft font-sans">{proposals.length}</span>
          )}
        </h2>
        {bulk.label !== null && (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <Button size="sm" variant="secondary" data-testid="confirm-queue-bulk" onClick={addAll}>
              {bulk.label}
            </Button>
            {bulk.skippedLine !== null && (
              <p data-testid="confirm-queue-bulk-skipped" className="text-xs text-ink-soft">
                {bulk.skippedLine}
              </p>
            )}
          </div>
        )}
      </div>

      {resolveError != null && <ErrorBanner error={resolveError} kind="write" />}

      <AsyncBoundary what="the suggestions"
        isLoading={isPending}
        isError={isError}
        error={error}
        onRetry={() => void refetch()}
        isEmpty={data !== undefined && proposals.length === 0}
        emptyHeadline="All confirmed."
        emptyBody="When you post a note, you'll see anything worth confirming here."
      >
        <div
          key={`settle-${settleTick}`}
          data-settled={settleTick > 0 ? 'true' : 'false'}
          className={`space-y-4 ${settleTick > 0 ? 'animate-settle-up motion-reduce:animate-none' : ''}`}
        >
          {high.length > 0 && (
            <ul data-testid="confirm-queue-high" className="space-y-3">
              {high.map(renderCard)}
            </ul>
          )}
          {low.length > 0 && (
            <div
              data-testid="confirm-queue-low"
              className="space-y-3 border-t border-warm-400/40 pt-3"
            >
              <p data-testid="confirm-queue-low-heading" className="text-xs text-caution-deep font-semibold">
                These need a closer look, so they're held for you. Check each one, then confirm or dismiss it.
              </p>
              <ul className="space-y-3">{low.map(renderCard)}</ul>
            </div>
          )}
        </div>
      </AsyncBoundary>
    </section>
  );
}
